import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// AlarmsPage.jsx - WEBSOCKET VERSION: Usa useAlarmSubscription en lugar de useAlarmMonitoring
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Grid,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
// 🔄 WEBSOCKET: Cambiar useAlarmMonitoring por useAlarmSubscription
import { useAlarmSubscription } from '../hooks/useWebSocket';
import { api } from '../services/api';
import AlarmList from '../components/alarms/AlarmList';
import AlarmForm from '../components/alarms/AlarmForm';
import StatsCard from '../components/dashboard/StatsCard';
import LoadingPage from '../components/common/LoadingPage';

const AlarmsPage = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const [tabValue, setTabValue] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [metricFilter, setMetricFilter] = useState('');
  const [recentCheckResult, setRecentCheckResult] = useState(null);

  // 🔄 WEBSOCKET: Usar useAlarmSubscription en lugar de useAlarmMonitoring
  // Cambio: activeAlarms → alarms, alarmCount → activeAlarmCount
  const { alarms: activeAlarms, activeAlarmCount: alarmCount } = useAlarmSubscription();

  // Crear parámetros limpios para la API
  const getApiParams = () => {
    const params = {
      active_only: tabValue === 0
    };

    if (vehicleFilter && vehicleFilter !== '' && vehicleFilter !== 'undefined') {
      params.vehicle_id = vehicleFilter;
    }

    if (metricFilter && metricFilter !== '' && metricFilter !== 'undefined') {
      params.metric_type = metricFilter;
    }

    return params;
  };

  // API calls
  const {
    data: alarms,
    loading: alarmsLoading,
    error: alarmsError,
    refetch: refetchAlarms
  } = useApiCall(() => api.alarms.getAll(getApiParams()), [tabValue, vehicleFilter, metricFilter]);

  const {
    data: vehicles,
    loading: vehiclesLoading
  } = useApiCall(api.vehicles.getAll);

  const {
    data: alarmStatus,
    loading: statusLoading
  } = useApiCall(api.alarms.getActiveStatus);

  const {
    data: dashboardSummary,
    loading: summaryLoading,
    refetch: refetchSummary
  } = useApiCall(api.alarms.getDashboardSummary);

  // Mutation para verificar telemetría reciente
  const { mutate: checkRecentTelemetry, loading: checkingTelemetry } = useMutation(
    api.alarms.checkRecentTelemetry,
    {
      onSuccess: (data) => {
        setRecentCheckResult(data);
        refetchAlarms();
        refetchSummary();
      },
      onError: (error) => {
        console.error('Error checking recent telemetry:', error);
      }
    }
  );

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreateAlarm = () => {
    setSelectedAlarm(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedAlarm(null);
  };

  const handleRefresh = () => {
    refetchAlarms();
    refetchSummary();
  };

  const handleVehicleFilterChange = (value) => {
    setVehicleFilter(value);
  };

  const handleMetricFilterChange = (value) => {
    setMetricFilter(value);
  };

  const handleCheckRecentTelemetry = () => {
    checkRecentTelemetry(5); // Check last 5 minutes
  };

  // Filter alarms based on search term
  const filteredAlarms = (alarms || []).filter(alarm => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      alarm.vehicle_name?.toLowerCase().includes(searchLower) ||
      alarm.metric_type?.toLowerCase().includes(searchLower) ||
      alarm.display_name?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate alarm statistics from dashboard summary or fallback to local calculation
  const totalAlarms = dashboardSummary?.total_alarms || alarms?.length || 0;
  const activeAlarmsCount = dashboardSummary?.active_alarms || alarms?.filter(a => a.active)?.length || 0;
  const criticalAlarms = dashboardSummary?.critical_alarms || alarmStatus?.filter(a => a.status === 'CRITICAL')?.length || 0;
  const vehiclesWithAlarms = dashboardSummary?.vehicles_with_active_alarms || 0;

  // 🔄 WEBSOCKET: Combinar estadísticas con datos en tiempo real
  const realtimeCritical = activeAlarms?.filter(a => a.status === 'CRITICAL' || a.severity === 'critical')?.length || 0;

  // Show loading if initial load is happening
  if (alarmsLoading && !alarms) {
    return <LoadingPage message={t('hardcoded.pages_alarmspage.cargando_alarmas')} />;
  }

  // Show error state if there's a persistent error
  if (alarmsError && !alarms) {
    return (
      <Container maxWidth={false} disableGutters>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('hardcoded.pages_alarmspage.error_al_cargar_las_alarmas')}</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {alarmsError.message || t('hardcoded.components_alarms_alarmform.ocurrio_un_error_inesperado')}
          </Typography>
          <Button variant="contained" onClick={handleRefresh} startIcon={<RefreshIcon />}>
            {i18n.t('hardcoded.pages_alarmspage.intentar_nuevamente')}</Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('hardcoded.pages_alarmspage.sistema_de_alarmas')}</Typography>
          {/* 🔄 WEBSOCKET: Indicador de alarmas en tiempo real */}
          {alarmCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={t('hardcoded.pages_alarmspage.alarmcount_alarmaalarmcount1s_activaalarmcount1s_en_tiempo', { alarmCount: alarmCount, alarmCount1s: alarmCount !== 1 ? 's' : '', alarmCount1s: alarmCount !== 1 ? 's' : '' })}
              color="error"
              size="small"
              sx={{ animation: 'pulse 2s infinite' }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={t('hardcoded.pages_alarmspage.verificar_telemetria_reciente')}>
            <Button
              variant="outlined"
              onClick={handleCheckRecentTelemetry}
              disabled={checkingTelemetry}
              startIcon={<SearchIcon />}
            >
              {checkingTelemetry ? t('hardcoded.pages_alarmspage.verificando') : t('hardcoded.pages_alarmspage.verificar_datos')}
            </Button>
          </Tooltip>

          <Tooltip title={t('hardcoded.components_schematics_vehicleroutemapmodal.actualizar_datos')}>
            <IconButton onClick={handleRefresh} color="primary" disabled={alarmsLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {hasPermission('manager') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateAlarm}
              disabled={vehiclesLoading}
            >
              {t('hardcoded.components_alarms_alarmlist.crear_alarma')}</Button>
          )}
        </Box>
      </Box>

      {/* Recent Check Result Alert */}
      {recentCheckResult && (
        <Alert
          severity={recentCheckResult.alarms_triggered > 0 ? "warning" : "success"}
          sx={{ mb: 3 }}
          onClose={() => setRecentCheckResult(null)}
        >
          <Typography variant="body2" fontWeight={600}>
            {t('hardcoded.pages_alarmspage.verificacion_completada')}</Typography>
          <Typography variant="body2">
            {recentCheckResult.checked_records} {i18n.t('hardcoded.pages_alarmspage.registros_procesados')}{recentCheckResult.alarms_triggered} {i18n.t('hardcoded.pages_alarmspage.alarmas_disparadas')}{recentCheckResult.emails_sent > 0 && t('hardcoded.pages_alarmspage.emails_sent_emails_enviados', { emails_sent: recentCheckResult.emails_sent })}
          </Typography>
          {recentCheckResult.vehicles_with_alarms?.length > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {t('hardcoded.pages_alarmspage.vehiculos_con_alarmas')}{recentCheckResult.vehicles_with_alarms.join(', ')}
            </Typography>
          )}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={t('hardcoded.pages_alarmspage.total_de_alarmas')}
            value={totalAlarms}
            icon={<WarningIcon />}
            color="primary"
            loading={summaryLoading || alarmsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={t('navbar.activeAlarms')}
            value={activeAlarmsCount}
            icon={<CheckIcon />}
            color="success"
            loading={summaryLoading || alarmsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={t('hardcoded.components_schematics_positiontile.alarmas_criticas')}
            value={Math.max(criticalAlarms, realtimeCritical)}
            icon={<WarningIcon />}
            color="error"
            trend={criticalAlarms > 0 || realtimeCritical > 0 ? 'up' : 'neutral'}
            trendValue={criticalAlarms > 0 || realtimeCritical > 0 ? t('hardcoded.pages_alarmspage.requiere_atencion') : t('hardcoded.pages_alarmspage.todo_normal')}
            loading={summaryLoading || statusLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={t('hardcoded.pages_alarmspage.vehiculos_con_alarmas')}
            value={vehiclesWithAlarms}
            icon={<InfoIcon />}
            color="info"
            loading={summaryLoading}
          />
        </Grid>
      </Grid>

      {/* Filters and Tabs */}
      <Paper sx={{ mb: 3 }}>
        {/* Filter Controls */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('hardcoded.pages_alarmspage.buscar_alarmas')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                disabled={alarmsLoading}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('vehicles.table.vehicle')}</InputLabel>
                <Select
                  value={vehicleFilter}
                  onChange={(e) => handleVehicleFilterChange(e.target.value)}
                  label={t('vehicles.table.vehicle')}
                  disabled={vehiclesLoading || alarmsLoading}
                >
                  <MenuItem value="">{t('hardcoded.components_alarms_alarmlist.todos_los_vehiculos')}</MenuItem>
                  {(vehicles || []).map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('hardcoded.components_alarms_alarmform.tipo_de_metrica')}</InputLabel>
                <Select
                  value={metricFilter}
                  onChange={(e) => handleMetricFilterChange(e.target.value)}
                  label={t('hardcoded.components_alarms_alarmform.tipo_de_metrica')}
                  disabled={alarmsLoading}
                >
                  <MenuItem value="">{t('hardcoded.pages_alarmspage.todas_las_metricas')}</MenuItem>
                  <MenuItem value="PSI">{t('hardcoded.components_alarmprofiles_profilecard.presion')}</MenuItem>
                  <MenuItem value="TEMP">{i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</MenuItem>
                  <MenuItem value="ACC">{t('hardcoded.components_alarmprofiles_profileform.acelerometro')}</MenuItem>
                  <MenuItem value="GPS">{t('hardcoded.pages_alarmspage.gps')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon />
                <Typography variant="body2" color="text.secondary">
                  {filteredAlarms?.length || 0} {i18n.t('hardcoded.pages_alarmspage.encontradas')}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {i18n.t('navbar.activeAlarms')}{activeAlarmsCount > 0 && (
                  <Chip label={activeAlarmsCount} size="small" color="primary" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('hardcoded.components_alarms_alarmlist.todas_las_alarmas')}<Chip label={totalAlarms} size="small" variant="outlined" />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('hardcoded.pages_alarmspage.estado_en_tiempo_real')}{/* 🔄 WEBSOCKET: Mostrar contador de WebSocket */}
                {alarmCount > 0 && (
                  <Chip
                    label={alarmCount}
                    size="small"
                    color="error"
                    sx={{ animation: 'pulse 2s infinite' }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {/* Active/All Alarms Tabs */}
        {(tabValue === 0 || tabValue === 1) && (
          <AlarmList
            vehicleId={vehicleFilter || null}
            showCreateButton={hasPermission('manager')}
          />
        )}

        {/* Real-time Status Tab */}
        {tabValue === 2 && (
          <Box>
            {/* 🔄 WEBSOCKET: Mostrar alarmas en tiempo real del WebSocket */}
            {alarmCount > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600}>
                  🔴 {alarmCount} {i18n.t('alarmTickets.severity.alarm')}{alarmCount !== 1 ? 's' : ''} {i18n.t('hardcoded.pages_alarmspage.detectada')}{alarmCount !== 1 ? 's' : ''} {t('hardcoded.pages_alarmspage.en_tiempo_real_via_websocket')}</Typography>
              </Alert>
            )}

            {statusLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>{t('hardcoded.pages_alarmspage.cargando_estado_en_tiempo_real')}</Typography>
              </Box>
            ) : (alarmStatus && alarmStatus.length > 0) || (activeAlarms && activeAlarms.length > 0) ? (
              <Grid container spacing={3}>
                {/* 🔄 WEBSOCKET: Combinar alarmStatus con activeAlarms del WebSocket */}
                {[...(alarmStatus || []), ...(activeAlarms || []).map(wa => ({
                  alarm_id: wa.id || wa.alarm_id,
                  vehicle_name: wa.vehicleName || wa.vehicle_name,
                  metric_type: wa.metric || wa.metric_type,
                  axle: wa.position || wa.axle,
                  current_value: wa.currentValue || wa.current_value,
                  threshold_min: wa.thresholdMin || wa.threshold_min,
                  threshold_max: wa.thresholdMax || wa.threshold_max,
                  status: wa.status || (wa.severity === 'critical' ? 'CRITICAL' : 'WARNING'),
                  isRealtime: true
                }))].filter(Boolean).filter((item, index, self) =>
                  // Deduplicar por alarm_id
                  index === self.findIndex(entry => entry && item && entry.alarm_id === item.alarm_id)
                ).map((status, index) => (
                  <Grid item xs={12} sm={6} md={4} key={status.alarm_id || index}>
                    <Paper sx={{ p: 3, position: 'relative' }}>
                      {/* 🔄 WEBSOCKET: Indicador de tiempo real */}
                      {status.isRealtime && (
                        <Chip
                          label={t('hardcoded.pages_alarmspage.live')}
                          size="small"
                          color="error"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontSize: '0.6rem',
                            height: 20,
                            animation: 'pulse 1s infinite'
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: status.status === 'CRITICAL' ? 'error.main' :
                              status.status === 'WARNING' ? 'warning.main' : 'success.main',
                            mt: 0.5,
                            animation: status.status !== 'OK' ? 'pulse 1s infinite' : 'none'
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {status.vehicle_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {status.metric_type} {i18n.t('hardcoded.components_alarms_alarmform.-_eje')}{status.axle}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              {i18n.t('hardcoded.pages_alarmspage.valor_actual')}{status.current_value !== null ?
                                (typeof status.current_value === 'number' ? status.current_value.toFixed(2) : status.current_value)
                                : t('hardcoded.pages_alarmspage.not_available')}
                            </Typography>
                            <Typography variant="body2">
                              Rango: {status.threshold_min || t('hardcoded.pages_alarmspage.not_available')} - {status.threshold_max || t('hardcoded.pages_alarmspage.not_available')}
                            </Typography>
                          </Box>
                          <Chip
                            label={status.status}
                            size="small"
                            color={
                              status.status === 'CRITICAL' ? 'error' :
                                status.status === 'WARNING' ? 'warning' : 'success'
                            }
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('hardcoded.pages_alarmspage.todos_los_sistemas_operando_normalmente')}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('navbar.noActiveAlarms')}</Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      {/* Alarm Form Dialog */}
      <AlarmForm
        open={formOpen}
        onClose={handleFormClose}
        alarm={selectedAlarm}
        onSuccess={() => {
          refetchAlarms();
          refetchSummary();
        }}
      />

      {/* 🔄 WEBSOCKET: Animación para indicadores en tiempo real */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Container>
  );
};

export default AlarmsPage;